import { forwardRef } from 'react';
import { combineClassNames } from '../../../Utils/ClassnameUtils';

export interface GWPInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
	size?: 'sm' | 'md' | 'lg';
	invalid?: boolean;
	startAdornment?: React.ReactNode;
	endAdornment?: React.ReactNode;
	onValueChange?: (value: string, event: React.ChangeEvent<HTMLInputElement>) => void;
	onEnter?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
}

const GWPInput = forwardRef<HTMLInputElement, GWPInputProps>((props, ref) => {
	const {
		className,
		size = 'md',
		type = 'text',
		invalid = false,
		startAdornment,
		endAdornment,
		onChange,
		onKeyDown,
		onValueChange,
		onEnter,
		disabled,
		readOnly,
		...rest
	} = props;

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		onChange?.(event);
		onValueChange?.(event.target.value, event);
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		onKeyDown?.(event);
		if (event.key === 'Enter') {
			onEnter?.(event);
		}
	};

	const rootClassName = combineClassNames(
		'gwp-input',
		size,
		invalid ? 'invalid' : undefined,
		disabled ? 'disabled' : undefined,
		readOnly ? 'read-only' : undefined,
		startAdornment ? 'has-start-adornment' : undefined,
		endAdornment ? 'has-end-adornment' : undefined,
		className,
	);

	return (
		<div className={rootClassName}>
			{startAdornment !== undefined && <span className="gwp-input-adornment start-adornment">{startAdornment}</span>}
			<input
				{...rest}
				ref={ref}
				type={type}
				disabled={disabled}
				readOnly={readOnly}
				aria-invalid={invalid || rest['aria-invalid']}
				className="gwp-input-control"
				onChange={handleChange}
				onKeyDown={handleKeyDown}
			/>
			{endAdornment !== undefined && <span className="gwp-input-adornment end-adornment">{endAdornment}</span>}
		</div>
	);
});

GWPInput.displayName = 'GWPInput';

export default GWPInput;
